import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
  InternalAxiosRequestConfig,
} from 'axios';
import { uuid } from 'common/utils';
import { updateKeycloakToken } from 'modules/keycloak';

import type Keycloak from 'keycloak-js';

/**
 * TODO Handle unknown server errors in store middleware as there is build warning
 * src/store/index.ts is dynamically imported by AjaxService.ts but also statically imported by ...
 */
// const storeImport = import('store');

type FetchConfig = RequestInit & AxiosRequestConfig;

interface AjaxServiceI {
  axios: AxiosInstance;
  keycloakService: Pick<Keycloak, 'token'>;
}

interface AjaxServiceAttributes {
  keycloakService: Keycloak;
}
class AjaxService implements AjaxServiceI {
  axios: AxiosInstance;
  keycloakService: Keycloak;
  numberOfPendingRequests = 0;

  constructor({ keycloakService }: AjaxServiceAttributes) {
    this.keycloakService = keycloakService;
    this.axios = axios.create({ timeout: 60000, paramsSerializer: { indexes: null } }); // TODO make it for natter api only
    this.axios.interceptors.request.use(this.useToken);
    this.axios.interceptors.request.use(this.useXRequestIdHeader);
    this.axios.interceptors.response.use(this.useRedirectUnauthorized, this.useRedirectUnauthorized);
  }

  useRedirectUnauthorized = (data: AxiosResponse | AxiosError): AxiosResponse | Promise<never> => {
    if ((data as AxiosError).response?.status === 401 && this.keycloakService.authenticated) {
      this.keycloakService.logout();
    }

    return axios.isAxiosError(data) ? Promise.reject(data) : data;
  };

  useToken: (value: InternalAxiosRequestConfig<unknown>) => Promise<InternalAxiosRequestConfig<unknown>> = async (
    axiosConfig
  ) => {
    if (!this.keycloakService.authenticated) {
      return axiosConfig;
    }

    await updateKeycloakToken(this.keycloakService);

    const authToken = this.keycloakService.token;
    const modifiedConfig = { ...axiosConfig };
    modifiedConfig.headers.Authorization = authToken ? `Bearer ${authToken}` : null;
    return modifiedConfig;
  };

  useXRequestIdHeader = async (
    axiosConfig: InternalAxiosRequestConfig<unknown>
  ): Promise<InternalAxiosRequestConfig<unknown>> => {
    axiosConfig.headers['X-Request-ID'] = uuid();
    return axiosConfig;
  };

  initCancelableRequest = <T = unknown>(): {
    cancelableRequest: (url: string, config?: FetchConfig) => Promise<AxiosResponse<T>>;
    cancelRequest: () => void;
  } => {
    let source: CancelTokenSource;

    const cancelableRequest = async (url: string, config?: FetchConfig): Promise<AxiosResponse<T>> => {
      source?.cancel();

      source = axios.CancelToken.source();

      return this.axios.request({
        ...config,
        url,
        cancelToken: source.token,
      });
    };

    const cancelRequest = () => {
      source?.cancel();
    };

    return {
      cancelableRequest,
      cancelRequest,
    };
  };
}

export default AjaxService;
